import React, { useMemo, useRef, useState, useCallback } from 'react';
import { View, StyleSheet, Pressable, Platform, TextInput, ActionSheetIOS } from 'react-native';
import ModalLayout from './ModalLayout';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore, useFileStore, useToastStore, useModelStore, useAuthStore, useChatStore, useChatInputStore } from '@/store';
import { uploadFileWithPlaceholder, uploadFilesWithPlaceholders } from '@/lib/uploadFileWithPlaceholder';
import socketManager from '@/lib/socket';
import { MAX_FILES_UPLOADED_IN_MESSAGE, DEFAULT_FONT_FAMILY } from '@/constants/config';
import { areTextToolsDisabledForSelection } from '@/constants/utils/tools';
import DateTimePicker from '@react-native-community/datetimepicker';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import Switch from '@/components/Inputs/Switch';
import { isFileParam } from '@/components/ChatInput/fileParamUtils';
import { formatCreditBalance, wholeCredits } from '@/lib/formatCredits';

function formatComposerCredits(credits) {
  const n = wholeCredits(credits);
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return formatCreditBalance(n);
}
const isAryaModel = (modelId) => {
  if (!modelId) return false;
  return modelId.toLowerCase().includes('arya');
};

// Web-only helper for styling HTML input[type="date"] elements
const createWebDateInputStyle = (colors) => ({
  flex: 1,
  backgroundColor: 'transparent',
  color: colors.text_color_primary,
  border: `1px solid ${colors.border_color_secondary}`,
  padding: 8,
  borderRadius: 6,
  fontFamily: DEFAULT_FONT_FAMILY,
});

// Styles for the reusable RowButton
const createRowButtonStyles = (colors) => StyleSheet.create({
  rowButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 42,
    paddingVertical: 11,
    paddingHorizontal: 16,
    backgroundColor: colors.solid_color_primary,
    borderBottomWidth: 0.5,
    borderBottomColor: colors.border_color_secondary,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }
    })
  },
  rowButtonDisabled: {
    opacity: 0.5,
  },
  rowLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    flexShrink: 1,
  },
  rowIcon: {
    marginTop: 1,
  },
  rowRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  rightText: {
    color: colors.text_color_secondary,
    fontSize: 13,
  },
  rowTitle: {
    color: colors.text_color_primary,
    fontSize: 14,
  },
  rowTitleContainer: {
    flexDirection: 'column',
    gap: 4,
  },
  rowSubtitle: {
    color: colors.text_color_tertiary,
    fontSize: 12,
  },
});

// Reusable row button component (top-level)
function RowButton({ colors, leftIcon, title, subtitle, rightElement, rightText, rightIcons = [], disabled = false, onPress }) {
  const styles = useMemo(() => createRowButtonStyles(colors), [colors]);
  return (
    <Pressable
      style={[styles.rowButton, disabled && styles.rowButtonDisabled]}
      disabled={disabled}
      onPress={onPress}
    >
      <View style={styles.rowLeft}>
        {!!leftIcon && <Icon name={leftIcon} size={14} color={colors.text_color_primary} style={styles.rowIcon} />}
        <View style={subtitle ? styles.rowTitleContainer : undefined}>
          {typeof title === 'string' ? (
            <Text style={styles.rowTitle}>{title}</Text>
          ) : (
            title
          )}
          {!!subtitle && <Text style={styles.rowSubtitle}>{subtitle}</Text>}
        </View>
      </View>
      <View style={styles.rowRight}>
        {rightElement ? (
          rightElement
        ) : (
          <>
            {!!rightText && <Text style={styles.rightText} numberOfLines={1} ellipsizeMode="tail">{rightText}</Text>}
            {rightIcons.filter(Boolean).map((iconName, idx) => (
              <Icon key={`rb-ri-${idx}`} name={iconName} size={14} color={colors.text_color_primary} />
            ))}
          </>
        )}
      </View>
    </Pressable>
  );
}

// Date range detail view for parameters with ui.component === 'daterange'
function DateRangeDetailView({ param, value, onChange, colors, onDone, styles }) {
  const parseDate = (str) => {
    if (!str || typeof str !== 'string') return null;
    const [y, m, d] = str.split('-').map(n => Number(n));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };
  const pad2 = (n) => String(n).padStart(2, '0');
  const fmt = (date) => {
    if (!date || isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = pad2(date.getMonth() + 1);
    const d = pad2(date.getDate());
    return `${y}-${m}-${d}`;
  };

  const initial = Array.isArray(value) ? value : (Array.isArray(param?.value) ? param.value : null);
  const [startDate, setStartDate] = React.useState(() => (initial ? parseDate(initial[0]) : null));
  const [endDate, setEndDate] = React.useState(() => (initial ? parseDate(initial[1]) : null));
  const [showStart, setShowStart] = React.useState(false);
  const [showEnd, setShowEnd] = React.useState(false);

  const propagate = (nextStart, nextEnd) => {
    if (nextStart && nextEnd) {
      onChange([fmt(nextStart), fmt(nextEnd)]);
    } else {
      onChange(null);
    }
  };

  const onStartChange = (_, selectedDate) => {
    setShowStart(false);
    if (selectedDate) {
      const next = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
      setStartDate(next);
      propagate(next, endDate);
    }
  };
  const onEndChange = (_, selectedDate) => {
    setShowEnd(false);
    if (selectedDate) {
      const next = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
      setEndDate(next);
      propagate(startDate, next);
    }
  };

  const clearDates = () => {
    setStartDate(null);
    setEndDate(null);
    onChange(null);
  };

  const webDateInputStyle = React.useMemo(() => createWebDateInputStyle(colors), [colors]);

  return (
    <View style={styles.dateDetailContainer}>
      {param?.validation?.required ? (
        <View style={styles.detailHeaderRow}>
          <View style={styles.requiredRow}>
            <Text style={styles.requiredBadge}>Required</Text>
            {(!!startDate && !!endDate) ? <Icon name="check" size={12} color="rgb(16, 185, 129)" /> : null}
          </View>
        </View>
      ) : null}
      {/* Start Date */}
      <View style={styles.dateRow}>
        <Text size='xs' color='secondary' style={styles.dateLabelSmall}>Start</Text>
        {Platform.OS === 'web' ? (
          <input
            type="date"
            value={startDate ? fmt(startDate) : ''}
            onChange={(e) => {
              const v = e.target.value;
              const d = parseDate(v);
              setStartDate(d);
              propagate(d, endDate);
            }}
            style={webDateInputStyle}
          />
        ) : (
          <>
            <Pressable
              style={styles.dateFieldButton}
              onPress={() => setShowStart(true)}
            >
              <Text>{startDate ? fmt(startDate) : 'Select start date'}</Text>
              <Icon name="calendar-range" size={14} color={colors.text_color_primary} />
            </Pressable>
            {showStart && (
              <DateTimePicker
                value={startDate || new Date()}
                mode="date"
                display={Platform.OS === 'ios' ? 'inline' : 'default'}
                onChange={onStartChange}
              />
            )}
          </>
        )}
      </View>

      {/* End Date */}
      <View style={styles.dateRow}>
        <Text size='xs' color='secondary' style={styles.dateLabelSmall}>End</Text>
        {Platform.OS === 'web' ? (
          <input
            type="date"
            value={endDate ? fmt(endDate) : ''}
            onChange={(e) => {
              const v = e.target.value;
              const d = parseDate(v);
              setEndDate(d);
              propagate(startDate, d);
            }}
            style={webDateInputStyle}
          />
        ) : (
          <>
            <Pressable
              style={styles.dateFieldButton}
              onPress={() => setShowEnd(true)}
            >
              <Text>{endDate ? fmt(endDate) : 'Select end date'}</Text>
              <Icon name="calendar-range" size={14} color={colors.text_color_primary} />
            </Pressable>
            {showEnd && (
              <DateTimePicker
                value={endDate || new Date()}
                mode="date"
                display={Platform.OS === 'ios' ? 'inline' : 'default'}
                onChange={onEndChange}
              />
            )}
          </>
        )}
      </View>

      {/* Actions */}
      <View style={styles.dateActionsRow}>
        <Pressable
          style={styles.clearActionButton}
          onPress={clearDates}
        >
          <Text size='s' color='primary'>Clear</Text>
        </Pressable>
        <Pressable
          style={styles.primaryActionButton}
          onPress={onDone}
        >
          <Text size='s' color='primary'>Done</Text>
        </Pressable>
      </View>
    </View>
  );
}

const EMPTY_SELECTED_PARAMETERS = {};

function ChatInputCustomParametersModal({
  onClose,
  embedded = false,
  onLowerActionsLayout,
  parameters = [],
  onParameterChange,
  selectedParameters = EMPTY_SELECTED_PARAMETERS,
  selectedAgentIds = [],
  selectedModelIds = [],
  selectedAgents = [],
  selectedModels = [],
  files = [],
  onAddFile,
  onAddUploadingFile,
  /** When set, modal opens directly to this parameter's detail view (e.g. from bar on native). */
  initialDetailParam = null,
  /** When set (and not 'new'), fast mode is read from/sent to the existing chat. */
  conversationId = null,
}) {
  const { colors } = useTheme();
  const { openModal, closeModal } = useModalStore(state => state.actions);
  const { uploadFile, isFileAllowed, fetchAllowedFileTypes, hasCreditsForFileUpload } = useFileStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);
  const fileInputRef = useRef(null);
  const hasReachedFileLimit = files?.length >= MAX_FILES_UPLOADED_IN_MESSAGE;
  const [localSelectedParameters, setLocalSelectedParameters] = React.useState(selectedParameters || {});
  const [openDropdownId, setOpenDropdownId] = React.useState(null);
  const [detailParam, setDetailParam] = React.useState(initialDetailParam || null);

  React.useEffect(() => {
    setLocalSelectedParameters(selectedParameters || {});
  }, [selectedParameters]);

  // Open directly to a parameter's detail view when initialDetailParam is provided (e.g. native bar tap).
  React.useEffect(() => {
    if (initialDetailParam && initialDetailParam.id) {
      setDetailParam(initialDetailParam);
    }
  }, [initialDetailParam?.id]);

  // Single-source-of-truth capability logic:
  // Determine if text tools (search-web, study-and-learn) should be disabled
  // based on the currently selected models and agents.
  const areTextToolsDisabled = useMemo(
    () => areTextToolsDisabledForSelection({ selectedModels, selectedAgents }),
    [selectedModels, selectedAgents]
  );

  // When tools become disabled, proactively clear text tools from the local
  // selection and notify the parent via onParameterChange so everything stays
  // in sync with the store. Do not overwrite the persisted Search Web preference.
  React.useEffect(() => {
    if (!areTextToolsDisabled) return;
    const current = localSelectedParameters?.tools;
    const currentArr = Array.isArray(current)
      ? current
      : (current ? [current] : []);

    const filtered = currentArr.filter(
      (v) => v !== 'search-web' && v !== 'study-and-learn'
    );

    if (filtered.length === currentArr.length) {
      // Nothing to clear
      return;
    }

    const next = filtered.length ? filtered : null;
    setLocalSelectedParameters(prev => ({ ...prev, tools: next }));
    onParameterChange?.('tools', next, { syncWebSearchPreference: false });
  }, [areTextToolsDisabled, localSelectedParameters?.tools, onParameterChange]);

  const canUseScreenCapture = useMemo(() => {
    if (Platform.OS !== 'web') return false;
    try {
      return !!(typeof navigator !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);
    } catch (_) {
      return false;
    }
  }, []);

  const styles = useMemo(() => StyleSheet.create({
    scroll: {
      flex: 1,
      ...(Platform.OS === 'macos' ? { flexBasis: 'auto', flexShrink: 0 } : Platform.OS !== 'web' ? { flexBasis: 0, minHeight: 0 } : {}),
    },
    container: {
      padding: embedded ? 0 : 10,
      gap: embedded ? 0 : 16,
    },
    gap10: {
      gap: 10,
    },
    section: {
      marginBottom: embedded ? 0 : 10,
    },
    sectionTitle: {
      textTransform: 'uppercase',
      marginBottom: 12,
      marginLeft: 16,
    },
    sectionTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 12,
      marginLeft: 16,
    },
    requiredAsteriskSmall: {
      color: 'rgb(255, 40, 40)',
      marginLeft: 6,
      fontSize: 12,
    },
    requiredBadge: {
      backgroundColor: 'rgba(255, 40, 40, 0.12)',
      color: 'rgb(255, 40, 40)',
      borderRadius: 6,
      paddingVertical: 2,
      paddingHorizontal: 6,
      fontSize: 10,
      textTransform: 'uppercase',
    },
    requiredRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    sectionList: {
      borderRadius: 8,
      borderWidth: embedded ? 0 : 0.5,
      borderColor: colors.border_color_secondary,
      overflow: 'hidden',
    },
    dropdownContent: {
      minWidth: 260,
      maxWidth: 420,
    },
    rightTap: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    hiddenInput: {
      display: 'none',
    },
    creditsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 10,
      paddingLeft: 15,
      paddingRight: 10,
      borderRadius: 8,
      borderWidth: 0.5,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_primary,
    },
    creditsLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    creditsValue: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text_color_primary,
    },
    creditsLabel: {
      fontSize: 13,
      color: colors.text_color_secondary,
    },
    buyButton: {
      paddingHorizontal: 12,
      paddingVertical: 5,
      borderRadius: 6,
      backgroundColor: colors.accent_color_primary,
    },
    buyButtonText: {
      fontSize: 13,
      fontWeight: '600',
      color: colors.accent_text_color,
    },
    numberRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 42,
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: 8,
      backgroundColor: colors.solid_color_tertiary,
      gap: 10,
    },
    numberControls: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    numberButton: {
      width: 32,
      height: 32,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 16,
      backgroundColor: colors.solid_color_primary,
      ...Platform.select({
        web: {
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }
      })
    },
    numberInput: {
      minWidth: 56,
      textAlign: 'center',
      fontSize: 15,
      color: colors.text_color_primary,
      backgroundColor: 'transparent',
      ...(Platform.OS === 'web' && { border: 'none', outlineStyle: 'none' }),
      paddingVertical: 4,
    },
    textarea: {
      backgroundColor: colors.solid_color_tertiary,
      color: colors.text_color_primary,
      borderRadius: 8,
      paddingHorizontal: 10,
      paddingVertical: 8,
      minHeight: 80,
      maxHeight: 240,
      lineHeight: 18,
      ...(Platform.OS === 'web' && { border: 'none', outlineStyle: 'none' }),
    },
    textareaLabel: {
      color: colors.text_color_secondary,
      fontSize: 12,
      marginBottom: 6,
    },
    textareaHeaderTextDescription: {
      color: colors.text_color_tertiary,
      fontSize: 12,
      marginBottom: 6,
    },
    textareaLengthText: {
      fontSize: 12,
      color: colors.text_color_tertiary,
      marginTop: 5,
      marginBottom: -10,
      fontStyle: 'italic',
    },
    fieldBlock: {
      gap: 6,
    },
    detailContainer: {
      paddingHorizontal: 5,
      paddingBottom: 20,
      gap: 12,
    },
    detailHeaderRow: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
    },
    // Date detail view styles
    dateDetailContainer: {
      paddingHorizontal: 6,
      paddingTop: 6,
      gap: 12,
    },
    dateRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    dateLabelSmall: {
      width: 56,
    },
    dateFieldButton: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 42,
      paddingVertical: 11,
      paddingHorizontal: 12,
      backgroundColor: colors.solid_color_tertiary,
      borderRadius: 8,
    },
    dateActionsRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: 8,
      marginTop: 4,
    },
    clearActionButton: {
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: 6,
      backgroundColor: 'transparent',
    },
    primaryActionButton: {
      paddingVertical: 8,
      paddingHorizontal: 12,
      borderRadius: 6,
      backgroundColor: colors.solid_color_primary,
    },
  }), [colors, embedded]);

  const user = useAuthStore(state => state.user);
  const totalAvailableCredits = user?.credits?.totalAvailable ?? 0;
  const monthlyCredits = user?.credits?.monthlyCredits ?? 0;
  const totalCredits = monthlyCredits + totalAvailableCredits;

  const handleBuyCredits = () => {
    openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
  };

  // Fast mode (same logic as ComposerToolsMenu)
  const isModelCompatible = useMemo(() => {
    if (!selectedModelIds || selectedModelIds.length === 0) return true;
    return selectedModelIds.every(isAryaModel);
  }, [selectedModelIds]);

  const isNewConversation = !conversationId || conversationId === 'new';
  const chatFastMode = useChatStore(state => {
    if (!conversationId || conversationId === 'new') return false;
    const chat = state.chats.find(c => c.id === conversationId);
    return chat?.flags?.fastMode || false;
  });
  const isFastMode = isNewConversation
    ? (localSelectedParameters?.fastMode ?? false)
    : chatFastMode;

  const handleToggleFastMode = useCallback(() => {
    if (!isModelCompatible) {
      addToast('Fast mode only available for Auto', 'default');
      return;
    }
    const newValue = !isFastMode;
    if (isNewConversation) {
      setLocalSelectedParameters(prev => ({ ...prev, fastMode: newValue }));
      onParameterChange?.('fastMode', newValue);
      addToast(newValue ? 'Fast mode on' : 'Fast mode off', 'default');
    } else {
      const userId = user?.id;
      if (!userId || !conversationId) {
        addToast('Unable to toggle fast mode. Please try again.', 'error');
        return;
      }
      socketManager.emitToggleFastMode(userId, conversationId, newValue);
      addToast(newValue ? 'Fast mode on' : 'Fast mode off', 'default');
    }
  }, [isModelCompatible, isFastMode, isNewConversation, onParameterChange, user?.id, conversationId, addToast]);

  const renderDetailView = () => {
    if (!detailParam) return null;
    const comp = detailParam?.ui?.component;
    const rawValue = localSelectedParameters?.[detailParam.id] ?? detailParam?.value;
    const updateValue = (next) => {
      setLocalSelectedParameters(prev => ({ ...prev, [detailParam.id]: next }));
      onParameterChange?.(detailParam.id, next);
    };

    if (comp === 'daterange') {
      return (
        <DateRangeDetailView
          param={detailParam}
          value={rawValue}
          onChange={updateValue}
          colors={colors}
          styles={styles}
          onDone={() => wasOpenedToDetail ? onClose?.() : setDetailParam(null)}
        />
      );
    }

    if (comp === 'number' || comp === 'slider') {
      const min = detailParam.validation?.number?.min ?? 0;
      const max = detailParam.validation?.number?.max ?? 100;
      const step = detailParam.validation?.number?.step ?? 1;
      const val = Number(rawValue ?? min);
      const toFixedPlaces = (() => {
        const stepStr = String(step);
        const i = stepStr.indexOf('.');
        return i === -1 ? 0 : (stepStr.length - i - 1);
      })();
      const clamp = (n) => Math.max(min, Math.min(max, n));
      return (
        <View style={styles.detailContainer}>
          {detailParam?.validation?.required ? (
            <View style={styles.detailHeaderRow}>
              <View style={styles.requiredRow}>
                <Text style={styles.requiredBadge}>Required</Text>
                {(Number.isFinite(val) && val >= min && val <= max) ? <Icon name="check" size={12} color="rgb(16, 185, 129)" /> : null}
              </View>
            </View>
          ) : null}
          <View style={styles.numberRow}>
            <Pressable style={styles.numberButton} onPress={() => updateValue(clamp(val - step))}>
              <Icon name="minus" size={14} color={colors.text_color_primary} />
            </Pressable>
            <TextInput
              style={styles.numberInput}
              value={Number.isFinite(val) ? val.toFixed(toFixedPlaces) : String(val ?? '')}
              onChangeText={(t) => {
                const cleaned = t.replace(/[^\d.-]/g, '');
                if (cleaned === '' || cleaned === '-' || cleaned === '.') return;
                updateValue(clamp(Number(cleaned)));
              }}
              keyboardType="numeric"
              {...Platform.select({
                web: { type: 'number', min, max, step }
              })}
            />
            <Pressable style={styles.numberButton} onPress={() => updateValue(clamp(val + step))}>
              <Icon name="plus" size={14} color={colors.text_color_primary} />
            </Pressable>
          </View>
        </View>
      );
    }

    if (comp === 'textarea') {
      const current = rawValue ?? '';
      const minLen = detailParam?.validation?.string?.minLength;
      const maxLen = detailParam?.validation?.string?.maxLength;
      return (
        <View style={styles.detailContainer}>
          {detailParam?.validation?.required ? (
            <View style={styles.detailHeaderRow}>
              <View style={styles.requiredRow}>
                <Text style={styles.requiredBadge}>Required</Text>
                {(() => {
                  const len = (current || '').length;
                  const minOk = typeof minLen === 'number' ? len >= minLen : len > 0;
                  const maxOk = typeof maxLen === 'number' ? len <= maxLen : true;
                  return (minOk && maxOk) ? <Icon name="check" size={12} color="rgb(16, 185, 129)" /> : null;
                })()}
              </View>
            </View>
          ) : null}
          {!!(detailParam?.ui?.description || detailParam?.description) && (
            <Text style={styles.textareaHeaderTextDescription}>
              {detailParam?.ui?.description || detailParam?.description}
            </Text>
          )}
          <TextInput
            multiline
            style={styles.textarea}
            value={current}
            onChangeText={(t) => updateValue(t)}
            placeholder={detailParam?.ui?.placeholder}
            placeholderTextColor={colors.text_color_tertiary}
            maxLength={maxLen || undefined}
            {...Platform.select({
              web: { minLength: minLen || undefined }
            })}
          />
          {(minLen || maxLen) && (
            <Text style={styles.textareaLengthText}>
              {`${(current || '').length}${maxLen ? `/${maxLen}` : ''}${minLen ? ` • min ${minLen}` : ''}`}
            </Text>
          )}
        </View>
      );
    }

    if (comp === 'textarea-multi') {
      const fields = Array.isArray(detailParam?.validation?.fields) ? detailParam.validation.fields : [];
      const current = typeof rawValue === 'object' && rawValue !== null ? rawValue : (detailParam.value || {});
      const handleChangeField = (key, val) => {
        const next = { ...(current || {}) };
        next[key] = val;
        updateValue(next);
      };
      return (
        <View style={[styles.detailContainer, styles.fieldBlock]}>
          {detailParam?.validation?.required ? (
            <View style={styles.detailHeaderRow}>
              <View style={styles.requiredRow}>
                <Text style={styles.requiredBadge}>Required</Text>
                {(() => {
                  const vals = Object.values(current || {});
                  const anyFilled = vals.some(v => typeof v === 'string' && v.trim().length > 0);
                  return anyFilled ? <Icon name="check" size={12} color="rgb(16, 185, 129)" /> : null;
                })()}
              </View>
            </View>
          ) : null}
          {!!(detailParam?.ui?.description || detailParam?.description) && (
            <Text style={styles.textareaHeaderTextDescription}>
              {detailParam?.ui?.description || detailParam?.description}
            </Text>
          )}
          <View style={styles.gap10}>
            {fields.map((f, i) => (
              <View key={`${detailParam.id}-${f.key || i}`} style={styles.fieldBlock}>
                {!!f.label && <Text style={styles.textareaLabel}>{f.label}</Text>}
                <TextInput
                  multiline
                  style={styles.textarea}
                  value={current?.[f.key] ?? ''}
                  onChangeText={(t) => handleChangeField(f.key, t)}
                  placeholder={f.placeholder}
                  placeholderTextColor={colors.text_color_tertiary}
                  maxLength={f.maxLength || undefined}
                />
                {!!f.description && (
                  <Text size='xs' color='tertiary'>{f.description}</Text>
                )}
              </View>
            ))}
          </View>
        </View>
      );
    }

    if (comp === 'select' || comp === 'action') {
      const options = Array.isArray(detailParam?.validation?.options) ? detailParam.validation.options : [];
      return (
        <View style={styles.detailContainer}>
          {detailParam?.validation?.required ? (
            <View style={styles.detailHeaderRow}>
              <View style={styles.requiredRow}>
                <Text style={styles.requiredBadge}>Required</Text>
                {(rawValue !== null && rawValue !== undefined && rawValue !== '') ? <Icon name="check" size={12} color="rgb(16, 185, 129)" /> : null}
              </View>
            </View>
          ) : null}
          <View style={styles.sectionList}>
            {options.map((option, idx) => (
              <RowButton
                key={`${detailParam.id}-${option.value}-${idx}`}
                colors={colors}
                leftIcon={option.icon}
                title={option.label}
                subtitle={option.description}
                rightIcons={[rawValue === option.value ? 'check' : null]}
                onPress={() => {
                  updateValue(option.value);
                  if (wasOpenedToDetail) {
                    onClose?.();
                  } else {
                    setDetailParam(null);
                  }
                }}
              />
            ))}
          </View>
        </View>
      );
    }

    if (comp === 'multiselect') {
      const options = Array.isArray(detailParam?.validation?.options) ? detailParam.validation.options : [];
      const currentValues = Array.isArray(rawValue) ? rawValue : (rawValue ? [rawValue] : []);
      return (
        <View style={styles.detailContainer}>
          {detailParam?.validation?.required ? (
            <View style={styles.detailHeaderRow}>
              <View style={styles.requiredRow}>
                <Text style={styles.requiredBadge}>Required</Text>
                {currentValues.length > 0 ? <Icon name="check" size={12} color="rgb(16, 185, 129)" /> : null}
              </View>
            </View>
          ) : null}
          <View style={styles.sectionList}>
            {options.map((option, idx) => {
              const isActive = currentValues.includes(option.value);
              return (
                <RowButton
                  key={`${detailParam.id}-${option.value}-${idx}`}
                  colors={colors}
                  leftIcon={option.icon}
                  title={option.label}
                  subtitle={option.description}
                  rightElement={
                    <Switch
                      value={isActive}
                      onValueChange={() => {
                        const next = isActive
                          ? currentValues.filter(v => v !== option.value)
                          : [...currentValues, option.value];
                        updateValue(next.length ? next : null);
                      }}
                    />
                  }
                  onPress={() => {
                    const next = isActive
                      ? currentValues.filter(v => v !== option.value)
                      : [...currentValues, option.value];
                    updateValue(next.length ? next : null);
                  }}
                />
              );
            })}
          </View>
        </View>
      );
    }

    if (comp === 'toggle') {
      const boolValue = typeof rawValue === 'boolean' ? rawValue : false;
      return (
        <View style={styles.detailContainer}>
          <RowButton
            colors={colors}
            leftIcon={detailParam?.ui?.icon}
            title={detailParam?.ui?.displayName || detailParam?.ui?.text || detailParam?.id}
            subtitle={detailParam?.description}
            rightElement={
              <Switch
                value={boolValue}
                onValueChange={(v) => {
                  updateValue(v);
                }}
              />
            }
            onPress={() => updateValue(!boolValue)}
          />
        </View>
      );
    }

    return <View style={styles.detailContainer} />;
  };

  const processFiles = useCallback(async (pickedFiles) => {
    await uploadFilesWithPlaceholders(pickedFiles, {
      uploadFile,
      isFileAllowed,
      addToast,
      onAddUploadingFile,
      onAddFile,
      extraTempFields: (file) => ({ previewFile: file }),
    });
  }, [isFileAllowed, uploadFile, addToast, onAddFile, onAddUploadingFile]);

  const handleFileInputChange = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) return;
    const { hasCredits, message: creditsMessage } = await hasCreditsForFileUpload();
    if (!hasCredits) {
      addToast(creditsMessage, 'error');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    await uploadFilesWithPlaceholders(selectedFiles, {
      uploadFile,
      isFileAllowed,
      addToast,
      onAddUploadingFile,
      onAddFile,
      extraTempFields: (file) => ({ previewFile: file }),
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUploadFile = useCallback(async () => {
    if (hasReachedFileLimit) {
      addToast(`You can attach up to ${MAX_FILES_UPLOADED_IN_MESSAGE} files per message`, 'error');
      return;
    }
    try { await fetchAllowedFileTypes(); } catch (err) { /* ignore */ }
    const { hasCredits, message: creditsMessage } = await hasCreditsForFileUpload();
    if (!hasCredits) {
      addToast(creditsMessage, 'error');
      return;
    }
    if (Platform.OS === 'web') {
      fileInputRef.current?.click();
      return;
    }
    onClose?.();
    if (Platform.OS === 'ios') {
      ActionSheetIOS.showActionSheetWithOptions(
        {
          options: ['Cancel', 'Take Photo', 'Choose from Photos', 'Browse Files'],
          cancelButtonIndex: 0,
        },
        async (buttonIndex) => {
          if (buttonIndex === 1) {
            const permission = await ImagePicker.requestCameraPermissionsAsync();
            if (permission.status !== 'granted') { addToast('Camera permission required', 'error'); return; }
            const result = await ImagePicker.launchCameraAsync({ mediaTypes: ['images'], quality: 0.8 });
            if (result.canceled) return;
            const asset = result.assets[0];
            await processFiles([{ uri: asset.uri, name: asset.fileName || `photo_${Date.now()}.jpg`, size: asset.fileSize || 0, type: asset.mimeType || 'image/jpeg' }]);
          } else if (buttonIndex === 2) {
            const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images', 'videos'], allowsMultipleSelection: true, quality: 0.8 });
            if (result.canceled) return;
            const picked = result.assets.map(a => ({ uri: a.uri, name: a.fileName || a.uri.split('/').pop() || 'photo.jpg', size: a.fileSize || 0, type: a.mimeType || 'image/jpeg' }));
            await processFiles(picked);
          } else if (buttonIndex === 3) {
            const result = await DocumentPicker.getDocumentAsync({ multiple: true, copyToCacheDirectory: true });
            if (result.canceled) return;
            const picked = result.assets?.map(a => ({ uri: a.uri, name: a.name, size: a.size, type: a.mimeType })) ?? (result.uri ? [{ uri: result.uri, name: result.name, size: result.size, type: result.mimeType }] : []);
            if (picked.length) await processFiles(picked);
          }
        }
      );
    } else {
      const result = await DocumentPicker.getDocumentAsync({ multiple: true, copyToCacheDirectory: true });
      if (result.canceled) return;
      const picked = result.assets?.map(a => ({ uri: a.uri, name: a.name, size: a.size, type: a.mimeType })) ?? (result.uri ? [{ uri: result.uri, name: result.name, size: result.size, type: result.mimeType }] : []);
      if (picked.length) await processFiles(picked);
    }
  }, [hasReachedFileLimit, addToast, fetchAllowedFileTypes, hasCreditsForFileUpload, onClose, processFiles]);

  const handleSelectFilesOption = (value) => {
    if (hasReachedFileLimit) {
      addToast(`You can attach up to ${MAX_FILES_UPLOADED_IN_MESSAGE} files per message`, 'error');
      return;
    }
    if (value === 'file-upload') {
      handleUploadFile();
      return;
    }
    if (value === 'file-library') {
      openModal('FilesModal', {
        onSelect: (picked) => {
          if (Array.isArray(picked)) {
            picked.forEach(f => onAddFile?.(f));
          } else if (picked) {
            onAddFile?.(picked);
          }
          closeModal();
        }
      });
      return;
    }
    if (value === 'file-screenshot') {
      if (!canUseScreenCapture) {
        addToast('Screen capture is not supported in this browser', 'error');
        return;
      }
      (async () => {
        try {
          try {
            await fetchAllowedFileTypes();
          } catch (error) {
            console.error('[ChatInputCustomParametersModal] Failed to fetch allowed file types for screenshot:', error);
            addToast('Unable to verify allowed file types. Some uploads may be rejected.', 'error');
          }
          const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
          const track = stream.getVideoTracks && stream.getVideoTracks()[0];
          const video = document.createElement('video');
          video.style.position = 'fixed';
          video.style.top = '-10000px';
          video.style.left = '-10000px';
          video.srcObject = stream;
          await new Promise((resolve) => {
            const onLoaded = () => resolve();
            video.onloadedmetadata = onLoaded;
            setTimeout(onLoaded, 500);
          });
          await video.play().catch((error) => {
            console.warn('[ChatInputCustomParametersModal] Video preview autoplay failed:', error);
          });
          const srcWidth = video.videoWidth || 1920;
          const srcHeight = video.videoHeight || 1080;
          const MAX_WIDTH = 1600;
          const MAX_HEIGHT = 1000;
          const scale = Math.min(1, MAX_WIDTH / srcWidth, MAX_HEIGHT / srcHeight);
          const width = Math.max(1, Math.round(srcWidth * scale));
          const height = Math.max(1, Math.round(srcHeight * scale));
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, width, height);
          const toBlob = (cnv, type, quality) => new Promise((resolve, reject) => {
            cnv.toBlob((b) => (b ? resolve(b) : reject(new Error('Failed to capture screenshot'))), type, quality);
          });
          let blob;
          let fileName;
          let mimeType;
          try {
            const jpegBlob = await toBlob(canvas, 'image/jpeg', 0.72);
            const jpegProbe = new File([jpegBlob], `screenshot-${Date.now()}.jpg`, { type: 'image/jpeg' });
            const { isAllowed: jpegAllowed } = isFileAllowed(jpegProbe);
            if (jpegAllowed) {
              blob = jpegBlob;
              fileName = jpegProbe.name;
              mimeType = 'image/jpeg';
            } else {
              const pngBlob = await toBlob(canvas, 'image/png');
              const pngProbe = new File([pngBlob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
              const { isAllowed: pngAllowed } = isFileAllowed(pngProbe);
              if (!pngAllowed) {
                addToast('Screenshots are not an allowed file type', 'error');
                return;
              }
              blob = pngBlob;
              fileName = pngProbe.name;
              mimeType = 'image/png';
            }
          } catch (e) {
            const pngBlob = await toBlob(canvas, 'image/png');
            blob = pngBlob;
            fileName = `screenshot-${Date.now()}.png`;
            mimeType = 'image/png';
          }
          try {
            if (track) track.stop();
            stream.getTracks && stream.getTracks().forEach(t => t.stop());
          } catch (e) {}
          const file = new File([blob], fileName, { type: mimeType });
          await uploadFileWithPlaceholder({
            file,
            uploadFile,
            isFileAllowed,
            addToast,
            onAddUploadingFile,
            onAddFile,
            extraTempFields: { previewFile: file },
          });
        } catch (err) {
          if (err && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
            addToast('Screen capture was canceled', 'default');
          } else {
            addToast(err?.message || 'Failed to capture screenshot', 'error');
          }
        }
      })();
      return;
    }
    // Fallback: just set parameter value
    onParameterChange?.('files', value);
  };

  const handleToggleTool = (param, optValue) => {
    const currentValues = Array.isArray(localSelectedParameters?.[param.id])
      ? localSelectedParameters[param.id]
      : (localSelectedParameters?.[param.id] ? [localSelectedParameters[param.id]] : []);
    const next = currentValues.includes(optValue)
      ? currentValues.filter(v => v !== optValue)
      : [...currentValues, optValue];
    setLocalSelectedParameters(prev => ({
      ...prev,
      [param.id]: next,
    }));
    onParameterChange?.(param.name, next);
  };

  const renderParamSection = (param) => {
    if (!param?.validation?.options) return null;
    const isFiles = param.id === 'files';
    const isTools = param.id === 'tools';
    if (!isFiles && !isTools) return null;

    // Show disabled state message when tools are disabled instead of blank
    if (isTools && areTextToolsDisabled) {
      return (
        <View key={`param-section-${param.id}`} style={styles.section}>
          <View style={styles.sectionTitleRow}>
            <Text size='xs' color='secondary'>
              {param?.ui?.displayName || param?.ui?.text || param.id}
            </Text>
          </View>
          <View style={[styles.sectionList, { paddingVertical: 16, paddingHorizontal: 16 }]}>
            <Text size='sm' color='tertiary' style={{ textAlign: 'center' }}>
              Tools are not available for image, audio, or video generation models.
            </Text>
          </View>
        </View>
      );
    }

    const model = param.modelId ? useModelStore.getState().getModelById(param.modelId) : null;

    const options = isFiles
      ? param.validation.options.filter(opt => opt.value !== 'file-screenshot')
      : param.validation.options;

    return (
      <View key={`param-section-${param.id}`} style={styles.section}>
        <View style={styles.sectionTitleRow}>
          <Text size='xs' color='secondary'>
          {param?.ui?.displayName || param?.ui?.text || param.id}
            {param?.validation?.required ? <Text style={styles.requiredAsteriskSmall}>*</Text> : null}
        </Text>
        </View>
        <View style={styles.sectionList}>
          {isTools && (
            <RowButton
              key={`${param.id}-fast-mode`}
              colors={colors}
              leftIcon="bolt"
              title="Fast mode"
              subtitle={isModelCompatible
                ? (isFastMode ? 'Skips intent engine for faster replies' : 'Skip intent engine for faster replies')
                : 'Only available for Auto model'}
              rightElement={
                <Switch
                  value={isFastMode}
                  onValueChange={handleToggleFastMode}
                  disabled={!isModelCompatible}
                />
              }
              disabled={!isModelCompatible}
              onPress={() => {
                if (!isModelCompatible) return;
                handleToggleFastMode();
              }}
            />
          )}
          {options.map((option, idx) => {
            const isActive = isTools
              ? (Array.isArray(localSelectedParameters?.[param.id]) && localSelectedParameters[param.id].includes(option.value))
              : false;
            const isDisabled = isFiles && option.value === 'file-screenshot' && !canUseScreenCapture;
            return (
              <RowButton
                key={`${param.id}-${option.value}-${idx}`}
                colors={colors}
                leftIcon={option.icon}
                title={option.label}
                subtitle={isTools ? option.description : undefined}
                rightElement={
                  isTools ? (
                    <Switch
                      value={isActive}
                      onValueChange={() => {
                        if (areTextToolsDisabled) return;
                        handleToggleTool(param, option.value);
                      }}
                      disabled={areTextToolsDisabled}
                    />
                  ) : null
                }
                rightIcons={!isTools ? [isActive ? 'check' : null, isFiles ? 'chevron-right' : null] : []}
                disabled={isDisabled || (isTools && areTextToolsDisabled)}
                onPress={() => {
                  if (isFiles) {
                    handleSelectFilesOption(option.value);
                  } else if (isTools) {
                    if (areTextToolsDisabled) return;
                    handleToggleTool(param, option.value);
                  }
                }}
              />
            );
          })}
        </View>
      </View>
    );
  };

  const isInDetail = !!detailParam;
  const wasOpenedToDetail = !!initialDetailParam;

  const handleDone = () => {
    if (isInDetail && !wasOpenedToDetail) {
      setDetailParam(null);
    } else {
      onClose?.();
    }
  };

  React.useEffect(() => {
    const actions = [{
      title: 'Done',
      onPress: handleDone,
    }];
    onLowerActionsLayout?.(actions);
  }, [isInDetail])

  const lowerActions = [{
    title: 'Done',
    onPress: handleDone,
  }];

  const Layout = embedded ? React.Fragment : ModalLayout;
  const layoutProps = embedded ? {} : {
    title: isInDetail ? (detailParam?.ui?.displayName || detailParam?.ui?.text || detailParam?.id) : undefined,
    hideCloseButton: !isInDetail,
    onClose: isInDetail ? (wasOpenedToDetail ? onClose : () => setDetailParam(null)) : onClose,
    onCloseIcon: wasOpenedToDetail ? 'xmark' : 'angle-left',
    lowerActions,
    background: isInDetail ? 'primary' : 'block',
    onLowerActionsLayout,
  };

  return (
    <Layout {...layoutProps}>
      {embedded && isInDetail && <RowButton colors={colors} leftIcon="angle-left" title="Back to parameters" onPress={() => setDetailParam(null)} />}
      {isInDetail ? (
        <View style={[styles.scroll, styles.container]}>
          {renderDetailView()}
        </View>
      ) : (
        <View
          style={[styles.scroll, styles.container]}
        >
          {!embedded && <View style={styles.creditsRow}>
            <View style={styles.creditsLeft}>
              <Icon name="coins" size={14} color={colors.text_color_secondary} />
              <Text style={styles.creditsValue}>{formatComposerCredits(totalCredits)}</Text>
              <Text style={styles.creditsLabel}>credits</Text>
            </View>
            <Pressable style={styles.buyButton} onPress={handleBuyCredits}>
              <Text style={styles.buyButtonText}>Buy more</Text>
            </Pressable>
          </View>}
          {!embedded && Array.isArray(parameters) && parameters.map((p) => (
            <React.Fragment key={`pfrag-${p.id}`}>
              {renderParamSection(p)}
            </React.Fragment>
          ))}
          {(() => {
            const filtered = Array.isArray(parameters) ? parameters.filter(p => !(p.id === 'files' || p.id === 'tools') && p?.ui?.component !== 'indicator' && !isFileParam(p)) : [];
            if (filtered.length === 0) return null;

            let groups = [];
            if (!selectedModels || selectedModels.length <= 1) {
              groups = [{ title: 'Parameters', items: filtered }];
            } else {
              const byModel = {};
              const general = [];
              filtered.forEach(p => {
                if (p.modelId) {
                  if (!byModel[p.modelId]) byModel[p.modelId] = [];
                  byModel[p.modelId].push(p);
                } else {
                  general.push(p);
                }
              });
              if (general.length > 0) groups.push({ title: 'Parameters', items: general });
              Object.keys(byModel).forEach(mId => {
                const m = useModelStore.getState().getModelById(mId);
                groups.push({ title: m ? `${m.name} Parameters` : 'Parameters', items: byModel[mId] });
              });
            }

            return groups.map((group, gIdx) => (
              <View key={`group-${gIdx}`} style={styles.section}>
                {!embedded && <Text size='xs' color='secondary' style={styles.sectionTitle}>{group.title}</Text>}
              <View style={styles.sectionList}>
                  {group.items.map((p) => {
                const model = p.modelId ? useModelStore.getState().getModelById(p.modelId) : null;
                const comp = p?.ui?.component;
                const rawValue = localSelectedParameters?.[p.id] ?? p?.value;
                let valueText = '';
                let rightElement = null;
                const hasDropdown = comp === 'select' || comp === 'action' || comp === 'multiselect';
                if (comp === 'daterange') {
                  const arr = Array.isArray(rawValue) ? rawValue : (Array.isArray(p?.value) ? p.value : []);
                  if (Array.isArray(arr) && arr.length === 2 && arr[0] && arr[1]) {
                    valueText = `${arr[0]} — ${arr[1]}`;
                  } else {
                    valueText = '';
                  }
                }
                if (comp === 'number' || comp === 'slider') {
                  const step = p?.validation?.number?.step;
                  if (typeof rawValue === 'number' && typeof step === 'number') {
                    const stepStr = String(step);
                    const i = stepStr.indexOf('.');
                    const places = i === -1 ? 0 : (stepStr.length - i - 1);
                    valueText = rawValue.toFixed(places);
                  } else if (typeof rawValue === 'number') {
                    valueText = String(rawValue);
                  }
                } else if (comp === 'toggle') {
                  if (typeof rawValue === 'boolean') {
                    rightElement = (
                      <Switch
                        value={rawValue}
                        onValueChange={(v) => {
                          setLocalSelectedParameters(prev => ({ ...prev, [p.id]: v }));
                          onParameterChange?.(p.id, v);
                        }}
                      />
                    );
                    valueText = '';
                  }
                } else if (comp === 'select' || comp === 'action') {
                  if (rawValue !== undefined && rawValue !== null) {
                    valueText = String(p.validation?.options?.find(option => option.value === rawValue)?.label ?? rawValue);
                  }
                } else if (comp === 'multiselect') {
                  const arr = Array.isArray(rawValue) ? rawValue : (rawValue ? [rawValue] : []);
                  if (arr.length === 1) {
                    valueText = String(arr[0]);
                  } else if (arr.length > 1) {
                    valueText = `${arr.length} selected`;
                  }
                } else {
                  // textarea / textarea-multi and other long-form inputs: no inline value
                  valueText = '';
                }
                if (hasDropdown) {
                  const options = Array.isArray(p?.validation?.options) ? p.validation.options : [];
                  const isMulti = comp === 'multiselect';
                  const selectValue = localSelectedParameters?.[p.id] ?? p.value;
                  const handleSelect = (optValue) => {
                    if (isMulti) {
                      const current = Array.isArray(selectValue) ? selectValue : (selectValue ? [selectValue] : []);
                      const next = current.includes(optValue)
                        ? current.filter(v => v !== optValue)
                        : [...current, optValue];
                      setLocalSelectedParameters(prev => ({ ...prev, [p.id]: next }));
                      onParameterChange?.(p.id, next);
                    } else {
                      setLocalSelectedParameters(prev => ({ ...prev, [p.id]: optValue }));
                      onParameterChange?.(p.id, optValue);
                    }
                  };
                  return (
                    <DropdownMenu.Root
                      key={`param-row-${p.id}`}
                      modal={embedded ? false : undefined}
                      open={openDropdownId === p.id}
                      onOpenChange={(next) => setOpenDropdownId(next ? p.id : null)}
                    >
                      <RowButton
                        colors={colors}
                        leftIcon={p?.ui?.icon}
                        onPress={embedded ? () => setOpenDropdownId(p.id) : undefined}
                        title={
                          <Text style={styles.rowTitle}>
                            {p?.ui?.displayName || p?.ui?.text || p.id}
                            {p?.validation?.required ? <Text style={styles.requiredAsteriskSmall}>*</Text> : null}
                          </Text>
                        }
                        rightElement={
                          <DropdownMenu.Trigger>
                            <Pressable style={styles.rightTap}>
                              {!!(valueText && !rightElement) && (
                                <Text style={styles.rightText} numberOfLines={1} ellipsizeMode="tail">
                                  {valueText}
                                </Text>
                              )}
                              <Icon name="angles-up-down" size={14} color={colors.text_color_primary} />
                            </Pressable>
                          </DropdownMenu.Trigger>
                        }
                      />
                      <DropdownMenu.Content
                        className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
                        style={styles.dropdownContent}
                      >
                        {options.map((opt, idx) => {
                          const isActive = isMulti
                            ? (Array.isArray(selectValue) && selectValue.includes(opt.value))
                            : selectValue === opt.value;

                          // The native menu only reads children whose element
                          // type is DropdownMenu.Item, so the wrapper is web-only.
                          if (Platform.OS !== 'web') {
                            return (
                              <DropdownMenu.Item
                                key={`${p.id}-${opt.value}-${idx}`}
                                onSelect={() => handleSelect(opt.value)}
                              >
                                {isActive && <DropdownMenu.ItemIcon ios={{ name: 'checkmark' }} />}
                                <DropdownMenu.ItemTitle>{String(opt.label ?? opt.value)}</DropdownMenu.ItemTitle>
                              </DropdownMenu.Item>
                            );
                          }

                          return (
                            <DropdownMenuItem
                              key={`${p.id}-${opt.value}-${idx}`}
                              title={opt.label}
                              icon={opt.icon}
                              isActive={isActive}
                              onSelect={() => handleSelect(opt.value)}
                              showInactiveIcon={isMulti}
                              inactiveIcon={isMulti && !isActive ? 'circle' : undefined}
                            />
                          );
                        })}
                      </DropdownMenu.Content>
                    </DropdownMenu.Root>
                  );
                }
                return (
                  <RowButton
                    key={`param-row-${p.id}`}
                    colors={colors}
                    leftIcon={p?.ui?.icon}
                    title={
                      <Text style={styles.rowTitle}>
                        {p?.ui?.displayName || p?.ui?.text || p.id}
                        {p?.validation?.required ? <Text style={styles.requiredAsteriskSmall}>*</Text> : null}
                      </Text>
                    }
                    rightElement={rightElement}
                    rightText={rightElement ? undefined : (valueText || undefined)}
                    rightIcons={rightElement ? [] : ['chevron-right']}
                    onPress={() => {
                      if (comp === 'toggle' && typeof rawValue === 'boolean') {
                        const next = !rawValue;
                        setLocalSelectedParameters(prev => ({ ...prev, [p.id]: next }));
                        onParameterChange?.(p.id, next);
                      } else {
                        setDetailParam(p);
                      }
                    }}
                  />
                );
              })}
            </View>
          </View>
            ));
          })()}
        {Platform.OS === 'web' && (
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            style={styles.hiddenInput}
            multiple
          />
        )}
      </View>
      )}
    </Layout>
  );
}

// Define default modal options
ChatInputCustomParametersModal.modalOptions = {
  width: 320,
  height: '100%',
  position: 'right',
  background: 'block',
  heightMobile: '90%',
};

export default ChatInputCustomParametersModal;